import { useState } from 'react';
import { createFileRoute, Navigate, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { enUS, ptBR } from 'date-fns/locale';
import { trpc } from '@org/clients/school';
import { Button } from '@org/ui';
import { FeatureFlagGate } from '../../../components/feature-flag-gate';
import {
  formatPrice,
  monthsFromDays,
  posterInkForProduct,
  posterStateOf,
  type PosterInk,
} from '../../../components/products/helpers';
import { CoverCarousel } from '../../../components/products/cover-carousel';
import { Poster } from '../../../components/products/poster';
import { PurchaseSheet, type PublicProduct } from '../../../components/products/purchase-sheet';
import { CheckoutSheet } from '../../../components/products/checkout-sheet';
import {
  P,
  PRODUCTS_STYLES,
  btnBlack,
  btnGhost,
  btnRed,
  dmSerif,
  eyebrowStyle,
  h2Style,
  h3Style,
  oswald,
  pressable,
  subStyle,
  useWidth,
} from '../../../components/products/theme';

export const Route = createFileRoute('/_app/products/$productId')({
  component: GatedProductPage,
});

function GatedProductPage() {
  return (
    <FeatureFlagGate flag="journey-products">
      <ProductPage />
    </FeatureFlagGate>
  );
}

type PublicUnit = NonNullable<PublicProduct['journey']>['units'][number];

// Thumbnail swatch for a cover without a photo: the poster's own ink.
const POSTER_SWATCH: Record<PosterInk, string> = {
  red: P.maxRed,
  pink: P.charm,
  black: P.black,
  cream: P.white,
};

// -- "O que está incluído" tile --
function IncludedTile({ value, label }: { value: string; label: string }) {
  return (
    <div
      style={{
        background: P.white,
        border: `1px solid ${P.lightBorder}`,
        borderRadius: 4,
        padding: '10px 12px',
      }}
    >
      <b
        style={{
          display: 'block',
          fontFamily: oswald,
          fontSize: 22,
          fontWeight: 700,
          lineHeight: 1,
          color: P.darkText,
        }}
      >
        {value}
      </b>
      <span
        style={{
          fontFamily: oswald,
          fontSize: 11,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: P.lightText,
        }}
      >
        {label}
      </span>
    </div>
  );
}

// -- "Conteúdo" row: number · unit name · classes · woman · quote · mini swatch --
const UNIT_SWATCHES = [P.maxRed, P.charm, P.black, P.white, P.sizzle];

function UnitRow({ unit, index, classesLabel }: { unit: PublicUnit; index: number; classesLabel: string }) {
  const swatch = UNIT_SWATCHES[index % UNIT_SWATCHES.length]!;
  const meta = [classesLabel, unit.womanName, unit.quote ? `“${unit.quote}”` : null]
    .filter(Boolean)
    .join(' · ');
  return (
    <li
      style={{
        display: 'flex',
        gap: 12,
        alignItems: 'center',
        padding: '10px 0',
        borderTop: `1px solid ${P.lightBorder}`,
      }}
    >
      <span
        style={{
          fontFamily: oswald,
          fontWeight: 700,
          fontSize: 20,
          color: P.charm,
          width: 32,
          flex: 'none',
        }}
      >
        {String(index + 1).padStart(2, '0')}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <b
          style={{
            display: 'block',
            fontFamily: oswald,
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            fontSize: 13,
            color: P.darkText,
          }}
        >
          {unit.name}
        </b>
        <span style={{ fontSize: 12, color: P.lightText, lineHeight: 1.4 }}>{meta}</span>
      </div>
      <div
        aria-hidden
        style={{
          width: 22,
          height: 30,
          borderRadius: 2,
          flex: 'none',
          background: swatch,
          border: swatch === P.white ? `1px solid ${P.lightBorder}` : undefined,
        }}
      />
    </li>
  );
}

// -- Page --
function ProductPage() {
  const { productId } = Route.useParams();
  const { t, i18n } = useTranslation('p2e');
  const navigate = useNavigate();
  const w = useWidth();
  const isMobile = w < 480;
  const dateLocale = i18n.language?.startsWith('pt') ? ptBR : enUS;
  const [sheetOpen, setSheetOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  const { data, isLoading, error } = trpc.products.getPublic.useQuery({ productId });

  const backLink = (
    <div
      {...pressable(() => navigate({ to: '/products' }))}
      className="products-link"
      style={{
        display: 'inline-block',
        fontFamily: oswald,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.18em',
        textTransform: 'uppercase',
        color: P.warmGray,
        cursor: 'pointer',
        marginBottom: 14,
      }}
    >
      {t('products.back')}
    </div>
  );

  if (isLoading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: P.cream,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <style dangerouslySetInnerHTML={{ __html: PRODUCTS_STYLES }} />
        <div
          role="status"
          aria-live="polite"
          style={{
            fontFamily: oswald,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.25em',
            textTransform: 'uppercase',
            color: P.warmGray,
          }}
        >
          {t('products.loading')}
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ minHeight: '100vh', background: P.cream }}>
        <style dangerouslySetInnerHTML={{ __html: PRODUCTS_STYLES }} />
        <div style={{ maxWidth: 960, margin: '0 auto', padding: '24px 16px' }}>
          {backLink}
          <div
            style={{
              background: P.white,
              border: `1.5px dashed ${P.lightBorder}`,
              borderRadius: 4,
              padding: '32px 20px',
              textAlign: 'center',
              color: P.midText,
              fontSize: 15,
            }}
          >
            {t('products.notFound')}
          </div>
        </div>
      </div>
    );
  }

  // The base access has no single journey; it is sold from the store hero.
  if (!data.journey) {
    return <Navigate to="/products" replace />;
  }
  const journey = data.journey;
  const { product, access } = data;
  const ink = posterInkForProduct(product.productId);
  const state = posterStateOf(access);
  const level = journey.levelName;
  const months = monthsFromDays(product.defaultDurationDays);
  const monthsLabel = months == null ? null : t('products.months', { count: months });
  const endsAtLabel = access.endsAt
    ? format(new Date(access.endsAt), 'PP', { locale: dateLocale })
    : null;
  const priceLabel =
    product.priceCents === 0
      ? t('products.free')
      : formatPrice(product.priceCents, product.currency, i18n.language ?? 'pt');

  const outcomes = product.outcomes ?? [];
  const gallery = (product.galleryImageUrls ?? []).slice(0, 4);
  const women = journey.units.filter((u) => !!u.womanName);
  const intro = product.audience || product.description;

  const coverTop =
    state === 'owned' && endsAtLabel
      ? t('products.untilDate', { date: endsAtLabel })
      : state === 'expired' && endsAtLabel
        ? t('products.endedDate', { date: endsAtLabel })
        : t('products.page.coverTag');
  const stamp =
    state === 'owned'
      ? { label: t('products.stamp.owned'), tone: 'green' as const }
      : state === 'expired'
        ? { label: t('products.stamp.expired'), tone: 'grey' as const }
        : state === 'pending'
          ? { label: t('products.stamp.pending'), tone: 'gold' as const, dashed: true }
          : undefined;

  const openJourney = () =>
    navigate({
      to: '/journey',
      search: { journeyId: journey.journeyId, celebrate: undefined, ceremony: undefined },
    });

  const cta =
    state === 'owned' ? (
      <Button type="button" variant="default" className="products-btn" style={btnBlack} onClick={openJourney}>
        {t('products.cta.continue')}
      </Button>
    ) : state === 'pending' ? (
      <Button type="button" variant="default" className="products-btn" style={btnBlack} disabled>
        {t('products.cta.pending')}
      </Button>
    ) : (
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        {/* Paid checkout (ASAAS) first; the teacher-unlock request stays as the fallback. */}
        <Button
          type="button"
          variant="red"
          className="products-btn"
          style={btnRed}
          onClick={() => setCheckoutOpen(true)}
          data-testid="product-buy"
        >
          {state === 'expired' ? t('products.cta.renew') : t('products.cta.buy')}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="products-btn"
          style={btnGhost}
          onClick={() => setSheetOpen(true)}
        >
          {t('products.cta.want')}
        </Button>
      </div>
    );

  return (
    <div style={{ minHeight: '100vh', background: P.cream }}>
      <style dangerouslySetInnerHTML={{ __html: PRODUCTS_STYLES }} />
      <div style={{ maxWidth: 960, margin: '0 auto', padding: '24px 16px 0' }}>
        {backLink}

        <div className="products-spread">
          {/* Left: the cover and its gallery, as one swipeable strip */}
          <div className="products-cover">
            <CoverCarousel
              label={t('products.page.galleryLabel')}
              gallery={gallery}
              coverThumbUrl={product.coverImageUrl}
              coverThumbFallback={
                <div
                  aria-hidden
                  style={{ width: '100%', height: '100%', background: POSTER_SWATCH[ink] }}
                />
              }
              cover={
                <Poster
                  ink={ink}
                  coverImageUrl={product.coverImageUrl}
                  headline={product.tagline || product.name}
                  topLeft={[level, coverTop].filter(Boolean).join(' · ')}
                  footLeft={product.name}
                  stamp={stamp}
                  state={state}
                  ariaLabel={product.name}
                />
              }
            />
          </div>

          {/* Right: the edition's story */}
          <div style={{ minWidth: 0 }}>
            <div style={eyebrowStyle}>
              {level
                ? t('products.page.eyebrow', { level })
                : t('products.page.eyebrowNoLevel')}
            </div>
            <h2 data-testid="product-heading" style={{ ...h2Style, fontSize: isMobile ? 26 : 30 }}>
              {product.name}
            </h2>
            {intro && <p style={subStyle}>{intro}</p>}

            {product.teacherNote && (
              <>
                <h3 style={h3Style}>{t('products.page.teacherNoteTitle')}</h3>
                <p
                  style={{
                    background: P.goldSoft,
                    borderLeft: `3px solid ${P.gold}`,
                    padding: '10px 14px',
                    borderRadius: '0 6px 6px 0',
                    fontSize: 14,
                    lineHeight: 1.55,
                    margin: 0,
                    maxWidth: '72ch',
                    color: P.darkText,
                    fontFamily: dmSerif,
                    fontStyle: 'italic',
                  }}
                >
                  {product.teacherNote}
                </p>
              </>
            )}

            {outcomes.length > 0 && (
              <>
                <h3 style={h3Style}>{t('products.page.outcomesTitle')}</h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 6px', maxWidth: '60ch' }}>
                  {outcomes.map((line, i) => (
                    <li
                      key={i}
                      style={{
                        position: 'relative',
                        padding: '6px 0 6px 30px',
                        borderTop: `1px dashed ${P.lightBorder}`,
                        fontSize: 15,
                        lineHeight: 1.55,
                        color: P.darkText,
                      }}
                    >
                      <span
                        aria-hidden
                        style={{
                          position: 'absolute',
                          left: 4,
                          top: 5,
                          color: P.maxRed,
                          fontFamily: oswald,
                          fontWeight: 700,
                          fontSize: 16,
                        }}
                      >
                        ✓
                      </span>
                      {line}
                    </li>
                  ))}
                </ul>
              </>
            )}

            <h3 style={h3Style}>{t('products.page.includedTitle')}</h3>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                gap: 8,
                margin: '0 0 6px',
              }}
            >
              {women.length > 0 && (
                <IncludedTile
                  value={String(women.length)}
                  label={t('products.page.womenToMeet', { count: women.length })}
                />
              )}
              <IncludedTile
                value={String(journey.classCount)}
                label={t('products.page.classesLabel', { count: journey.classCount })}
              />
              {months == null ? (
                <IncludedTile value="∞" label={t('products.page.noLimitAccess')} />
              ) : (
                <IncludedTile
                  value={String(months)}
                  label={t('products.page.monthsAccess', { count: months })}
                />
              )}
              <IncludedTile value="✓" label={t('products.page.feedback')} />
            </div>

            {journey.units.length > 0 && (
              <>
                <h3 style={h3Style}>{t('products.page.contentTitle')}</h3>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {journey.units.map((unit, i) => (
                    <UnitRow
                      key={unit.unitId}
                      unit={unit}
                      index={i}
                      classesLabel={t('products.classes', { count: unit.classCount })}
                    />
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>

        {/* Sticky buy bar */}
        <div
          style={{
            position: 'sticky',
            bottom: 0,
            zIndex: 5,
            background: P.white,
            borderTop: `2px solid ${P.black}`,
            margin: '26px -16px 0',
            padding: '12px 16px calc(12px + env(safe-area-inset-bottom, 0px))',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          {/* Fills what the button leaves so price + button share one row on phones (the label wraps first). */}
          <div style={{ fontFamily: oswald, minWidth: 0, flex: '1 1 0' }}>
            <small
              style={{
                display: 'block',
                fontSize: 11,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: P.lightText,
              }}
            >
              {state === 'owned'
                ? endsAtLabel
                  ? t('products.cta.openUntil', { date: endsAtLabel })
                  : t('products.cta.openNoLimit')
                : monthsLabel
                  ? t('products.cta.accessFor', { months: monthsLabel })
                  : t('products.cta.accessNoLimit')}
            </small>
            <b style={{ fontSize: 26, fontWeight: 700, color: P.darkText }}>{priceLabel}</b>
          </div>
          {cta}
        </div>
      </div>

      <PurchaseSheet open={sheetOpen} onOpenChange={setSheetOpen} data={{ ...data, journey }} ink={ink} />
      <CheckoutSheet
        open={checkoutOpen}
        onOpenChange={setCheckoutOpen}
        product={{
          productId: product.productId,
          name: product.name,
          priceCents: product.priceCents,
          currency: product.currency,
          billing: 'one_time',
          coverImageUrl: product.coverImageUrl,
        }}
      />
    </div>
  );
}
